import { RefObject, useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useReactiveVar } from '@apollo/client';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { cx } from '@emotion/css';

import { useModal } from 'mui-modal-provider';
import { useStyles } from './ecp-locator.styles';
import { EcpMap } from '../ecp-map';
import { EcpStores } from '../ecp-stores';
import { EcpSearch } from '../ecp-search';
import { EcpSort } from '../ecp-sort/ecp-sort';
import { StoreDetail } from './store-detail';
import { useStoreDetailFromCache } from '../../../../graphql/ecp-locator/actions/store-detail';
import { Page } from '../page/page';
import {
  ecpStoresCached,
  setAutocompletedSearch,
  setAutocompletedValue,
  setAutoValueSelected,
  setEcpFormSubmitted,
  setStoreCode,
  setStoreDetail,
  setSearchBounds,
} from '../../../../graphql/ecp-locator/ecp-locator.cache';
import { IconButton } from '../../../common/icon-button';
import { Icon, IconSizes, IconTypes } from '../../../common/icon';
import { EcpFiltersModal } from '../ecp-filters-modal';
import { IEcpStore } from '../../../../rest/ecp-locator';
import { ReactComponent as ShowListIcon } from '../../../../assets/images/showList.svg';
import { ReactComponent as ShowMapIcon } from '../../../../assets/images/showMap.svg';
import { IEcpLocatorProps } from './ecp-locator.models';
import { Preloader } from '../../../common/preloader';
import { ErrorPlain, ErrorTypes } from '../../../common/error-plain';
import { MobileViewTypes } from '../../../../graphql/ecp-locator/models/map.models';
import { Appointment } from '../user-activity-items/appointment';
import { setC2CMode } from '../../../../graphql/configuration/configuration.cache';
import { FilterButton } from './filter-button';
import { InternalLinks } from '../../../../constants';
import { v2025PP18603 } from '../../../../constants/veeva';
import { Veeva } from '../../../common/veeva';

export const EcpLocator = ({
  isFitted, isLoading, updateStores,
}: IEcpLocatorProps): JSX.Element => {
  const intl = useIntl();
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('xl'));
  const isTablet = !isMobile && !isDesktop;

  const [mobileView, setMobileView] = useState(MobileViewTypes.map);

  const { setStoreDetailToCache } = useStoreDetailFromCache();

  const ecpStores = useReactiveVar(ecpStoresCached);
  const storeDetail = useReactiveVar(setStoreDetail);
  const storeCode = useReactiveVar(setStoreCode);
  const isEcpFormSubmitted = useReactiveVar(setEcpFormSubmitted);
  const c2CMode = useReactiveVar(setC2CMode);

  const { showModal } = useModal();

  const isStoreListShow = !storeDetail;
  const isLayerCard = isTablet || (isMobile && isStoreListShow);
  const isMapHidden = isMobile && (mobileView !== MobileViewTypes.map || !isStoreListShow);
  const isStoresShow = !isMobile || mobileView === MobileViewTypes.list;
  const isMobileButtonsShow = isMobile && isStoreListShow;
  const inputIcon = isMobile ? undefined : <Icon type={IconTypes.map} />;
  const resultsRef = useRef<HTMLUListElement | null>(null);
  const isErrorStores = isEcpFormSubmitted && !ecpStores.length && isStoresShow;

  // Hide C2C feature from aohm-only fitted users
  const handleStoreSelect = (store: IEcpStore): void => {
    setStoreDetailToCache(store);
  };

  const handleStoreClose = (code: string) => {
    setStoreDetailToCache(null);
    // To manage scroll to store card.
    setStoreCode(code);
  };

  const handleScrollToStore = (
    currentStoreId: string,
    storeTitle: RefObject<HTMLHeadingElement>,
    storeId?: string,
  ) => {
    if (storeId?.length && storeId === currentStoreId) {
      storeTitle.current?.focus();
      storeTitle.current?.scrollIntoView();
      setStoreCode('');
    }
  };

  const handleFilterButtonClick = () => {
    showModal(EcpFiltersModal, {
      c2CMode,
    });
  };

  useEffect(() => {
    setAutocompletedValue('');
    setSearchBounds(null);

    return () => {
      setStoreDetailToCache(null);
      setStoreCode('');
      setStoreDetail(null);
      setAutocompletedValue('');
      setEcpFormSubmitted(false);
      setAutoValueSelected(false);
      setAutocompletedSearch(false);
      setSearchBounds(null);
    };
  }, []);

  return (
    <Page
      title={intl.formatMessage({ id: 'ecpLocatorPage.title' })}
      description={intl.formatMessage({ id: 'ecpLocatorPage.description' })}
    >
      <div
        className={cx(classes.root, { [classes.fullHeight]: mobileView === MobileViewTypes.list && isMobile && isStoresShow })}
        data-testid="ecp-locator"
      >
        <div
          className={cx(classes.ecpLayerRoot, {
            [classes.ecpLayerRootCard]: isLayerCard,
          })}
        >
          {isStoreListShow && (
            <>
              <div
                className={cx(classes.ecpLayerWrapper, classes.shadowWrapper)}
                data-testid="ecp-stores-container"
              >
                {!isMobile && (
                  <h1
                    className={classes.title}
                    data-testid="ecp-stores-title"
                  >
                    <FormattedMessage
                      id="ecp.title"
                    />
                  </h1>
                )}
                <div className={classes.searchBar}>
                  <EcpSearch
                    updateStores={updateStores!}
                    inputIcon={inputIcon}
                    inputStyles={classes.input}
                    rootInputStyles={classes.inputRoot}
                    isC2c={false}
                  />
                  <div className={classes.filterButtonDesktop}>
                    <FilterButton onClick={handleFilterButtonClick} />
                  </div>
                  <IconButton
                    aria-label={intl.formatMessage({ id: 'onlineShops.title' })}
                    to={InternalLinks.onlineShops}
                    iconProps={{
                      type: isMobile ? IconTypes.internetShops : IconTypes.internetShopsWrapped,
                      size: isMobile ? IconSizes.sm : IconSizes.lg,
                    }}
                  />
                </div>
                {isStoresShow && (
                  <div className={classes.sortButtons}>
                    <EcpSort />
                  </div>
                )}
              </div>
              {isErrorStores && (
                <div className={classes.ecpLayerWrapper}>
                  <ErrorPlain
                    content="ecp.error.noResults"
                    isolated
                    variant={ErrorTypes.plainText}
                  />
                </div>
              )}
              {isStoresShow && (
                <div className={cx(classes.ecpLayerWrapper, classes.storesList)}>
                  <>
                    {isLoading && <Preloader />}
                    {ecpStores?.length ? <EcpStores
                      ref={resultsRef}
                      stores={ecpStores}
                      onStoreSelect={handleStoreSelect}
                      storeCode={storeCode}
                      onScrollToStore={handleScrollToStore}
                    /> : <></>}
                  </>
                </div>
              )}
            </>
          )}
          {storeDetail && (
            <div className={classes.ecpLayerWrapper}>
              <StoreDetail
                storeData={storeDetail as IEcpStore}
                onStoreClose={handleStoreClose}
                updateStores={updateStores!}
                isFitted={isFitted!}
                c2CMode={c2CMode}
              />
            </div>
          )}
        </div>
        {!(mobileView === MobileViewTypes.list) && (
          <div
            data-testid="ecp-map-container"
            className={cx(classes.mapLayer,
              { [classes.mapLayerHidden]: isMapHidden },
            )}
          >
            <EcpMap
              ecpStores={ecpStores}
              storeDetail={storeDetail as IEcpStore}
              isMapHidden={isMapHidden}
            />
            <div
              data-testid="appointment-wrapper"
              className={classes.appointmentWrapper}
            >
              <Appointment />
            </div>
          </div>
        )}
        {isMobileButtonsShow && (
          <div className={cx(classes.mobileButtonsBar,
            { [classes.listButtons]: mobileView === MobileViewTypes.list && isErrorStores })}>
            <div className={classes.mobileButtonsCenter}>
              <FilterButton onClick={handleFilterButtonClick} />
            </div>
            <div className={classes.mobileViewButton}>
              <button
                className={classes.mobileViewButtonStyle}
                onClick={() => setMobileView(
                  mobileView === MobileViewTypes.map
                    ? MobileViewTypes.list
                    : MobileViewTypes.map,
                )}
              >
                {mobileView === MobileViewTypes.map ? (
                  <ShowListIcon width="24" height="24" />
                ) : (
                  <ShowMapIcon width="24" height="24" />
                )}
              </button>
            </div>
          </div>
        )}
        {(mobileView === MobileViewTypes.list) && (
          <Veeva veevaNumber={v2025PP18603} isLeft />
        )}
      </div>
    </Page>
  );
};
